/**
 * Comparable Films Intelligence — Shows similar produced films with market data.
 * Part of the Greenlight Engine for 1895.ai.
 */
import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Film, TrendingUp, Target, Clock } from 'lucide-react';

/* ─── helpers ─────────────────────────────────────────────────────── */

function scoreColor(score) {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}

function scoreBg(score) {
  if (score >= 80) return '#10B981';
  if (score >= 60) return '#F59E0B';
  if (score >= 40) return '#F97316';
  return '#EF4444';
}

function timingColor(assessment) {
  const lower = (assessment || '').toLowerCase();
  if (lower.includes('favorable') || lower.includes('favourable')) return 'text-emerald-400';
  if (lower.includes('unfavorable') || lower.includes('unfavourable')) return 'text-red-400';
  return 'text-amber-400';
}

function timingBg(assessment) {
  const lower = (assessment || '').toLowerCase();
  if (lower.includes('favorable') || lower.includes('favourable')) return 'bg-emerald-500/10 border-emerald-500/30';
  if (lower.includes('unfavorable') || lower.includes('unfavourable')) return 'bg-red-500/10 border-red-500/30';
  return 'bg-amber-500/10 border-amber-500/30';
}

/* ─── Circular Gauge (small) ─────────────────────────────────────── */

function MiniGauge({ value, max, color, label, size = 48 }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const normalized = Math.min(value / max, 1);
  const strokeDashoffset = circumference * (1 - normalized);

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={3}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
        <text
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontSize={size < 56 ? 11 : 14}
          fontWeight="600"
          transform={`rotate(90, ${size / 2}, ${size / 2})`}
        >
          {typeof value === 'number' && value % 1 !== 0 ? value.toFixed(1) : value}
        </text>
      </svg>
      <span className="text-[10px] text-white/50 uppercase tracking-wider">{label}</span>
    </div>
  );
}

/* ─── Opportunity Score Gauge (larger) ───────────────────────────── */

function OpportunityGauge({ score }) {
  const size = 96;
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const normalized = Math.min(score / 100, 1);
  const strokeDashoffset = circumference * (1 - normalized);
  const color = scoreBg(score);

  return (
    <div className="flex flex-col items-center gap-2">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={5}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={5}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
        <text
          x={size / 2}
          y={size / 2 - 4}
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontSize={22}
          fontWeight="700"
          transform={`rotate(90, ${size / 2}, ${size / 2})`}
        >
          {score}
        </text>
        <text
          x={size / 2}
          y={size / 2 + 14}
          textAnchor="middle"
          dominantBaseline="central"
          fill="rgba(255,255,255,0.45)"
          fontSize={9}
          transform={`rotate(90, ${size / 2}, ${size / 2})`}
        >
          / 100
        </text>
      </svg>
      <span className="text-xs text-white/50 uppercase tracking-wider">Opportunity</span>
    </div>
  );
}

/* ─── Similarity Score Badge (gold ring) ─────────────────────────── */

function SimilarityBadge({ score }) {
  const size = 44;
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const normalized = Math.min(score / 100, 1);
  const strokeDashoffset = circumference * (1 - normalized);

  return (
    <svg width={size} height={size} className="transform -rotate-90 shrink-0">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.08)"
        strokeWidth={3}
      />
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#FFD700"
        strokeWidth={3}
        strokeLinecap="round"
        strokeDasharray={circumference}
        initial={{ strokeDashoffset: circumference }}
        animate={{ strokeDashoffset }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill="#FFD700"
        fontSize={12}
        fontWeight="700"
        transform={`rotate(90, ${size / 2}, ${size / 2})`}
      >
        {score}
      </text>
    </svg>
  );
}

/* ─── Film Card ──────────────────────────────────────────────────── */

function FilmCard({ film, index }) {
  const bo = film.box_office || {};
  const ratings = film.audience_ratings || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1, ease: 'easeOut' }}
      className="rounded-lg border border-white/10 bg-white/[0.03] p-5 space-y-4"
    >
      {/* Title + similarity */}
      <div className="flex items-center gap-3">
        <SimilarityBadge score={film.similarity_score} />
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-white truncate">{film.title}</h3>
          <span className="text-xs text-white/40">
            {film.similarity_score}% match
          </span>
        </div>
      </div>

      {/* Box office grid */}
      {(bo.budget || bo.domestic || bo.worldwide || bo.roi) && (
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Budget', value: bo.budget },
            { label: 'Domestic', value: bo.domestic },
            { label: 'Worldwide', value: bo.worldwide },
            { label: 'ROI', value: bo.roi },
          ].map(
            (item) =>
              item.value && (
                <div
                  key={item.label}
                  className="rounded-md bg-white/[0.04] border border-white/5 px-2 py-2 text-center"
                >
                  <div className="text-[10px] text-white/40 uppercase tracking-wider mb-0.5">
                    {item.label}
                  </div>
                  <div className="text-sm font-semibold text-white">{item.value}</div>
                </div>
              )
          )}
        </div>
      )}

      {/* Audience ratings gauges */}
      {(ratings.rotten_tomatoes || ratings.imdb || ratings.metacritic) && (
        <div className="flex items-center gap-6 justify-center py-1">
          {ratings.rotten_tomatoes != null && (
            <MiniGauge
              value={ratings.rotten_tomatoes}
              max={100}
              color="#EF4444"
              label="RT"
            />
          )}
          {ratings.imdb != null && (
            <MiniGauge
              value={ratings.imdb}
              max={10}
              color="#FBBF24"
              label="IMDb"
            />
          )}
          {ratings.metacritic != null && (
            <MiniGauge
              value={ratings.metacritic}
              max={100}
              color="#10B981"
              label="MC"
            />
          )}
        </div>
      )}

      {/* Shared elements — green pills */}
      {film.shared_elements?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] text-white/40 uppercase tracking-wider">
            Shared Elements
          </div>
          <div className="flex flex-wrap gap-1.5">
            {film.shared_elements.map((el) => (
              <span
                key={el}
                className="inline-block rounded-full bg-emerald-500/15 text-emerald-400 text-xs px-2.5 py-0.5"
              >
                {el}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Differentiators — amber pills */}
      {film.differentiators?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] text-white/40 uppercase tracking-wider">
            Differentiators
          </div>
          <div className="flex flex-wrap gap-1.5">
            {film.differentiators.map((d) => (
              <span
                key={d}
                className="inline-block rounded-full bg-amber-500/15 text-amber-400 text-xs px-2.5 py-0.5"
              >
                {d}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Streaming performance */}
      {film.streaming_performance && (
        <div className="flex items-start gap-2 text-xs text-white/60">
          <TrendingUp size={14} className="shrink-0 mt-0.5 text-white/30" />
          <span>{film.streaming_performance}</span>
        </div>
      )}

      {/* Relevance note */}
      {film.relevance_note && (
        <p className="text-xs italic text-white/45 border-l-2 border-white/10 pl-3">
          {film.relevance_note}
        </p>
      )}
    </motion.div>
  );
}

/* ─── Market Gap Section ─────────────────────────────────────────── */

function MarketGapSection({ gap }) {
  if (!gap) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="rounded-lg border border-white/10 bg-white/[0.03] p-5 space-y-4"
    >
      <div className="flex items-center gap-2 mb-1">
        <Target size={16} className="text-white/50" />
        <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
          Market Gap Analysis
        </h3>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
        {/* Opportunity gauge */}
        {gap.opportunity_score != null && (
          <OpportunityGauge score={gap.opportunity_score} />
        )}

        <div className="flex-1 space-y-3 min-w-0">
          {/* Description */}
          {gap.description && (
            <p className="text-sm text-white/70 leading-relaxed">{gap.description}</p>
          )}

          {/* Timing assessment */}
          {gap.timing_assessment && (
            <div
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs ${timingBg(gap.timing_assessment)}`}
            >
              <Clock size={13} className={timingColor(gap.timing_assessment)} />
              <span className={timingColor(gap.timing_assessment)}>
                {gap.timing_assessment}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Competitive landscape */}
      {gap.competitive_landscape?.length > 0 && (
        <div className="space-y-2 pt-1">
          <div className="text-[10px] text-white/40 uppercase tracking-wider">
            Competitive Landscape
          </div>
          <ul className="space-y-1">
            {gap.competitive_landscape.map((item, i) => (
              <li
                key={i}
                className="flex items-center gap-2 text-xs text-white/60"
              >
                <span className="w-1 h-1 rounded-full bg-white/30 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────── */

export default function ComparableFilms({ data }) {
  const films = data?.comparable_films || [];
  const gap = data?.market_gap;
  const positioningSummary = data?.positioning_summary;

  const headerScore = gap?.opportunity_score;

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Film size={18} className="text-white/50" />
          <h2 className="text-lg font-semibold text-white tracking-tight">
            Comparable Films
          </h2>
        </div>
        {headerScore != null && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-white/40 uppercase tracking-wider">
              Market Gap
            </span>
            <span
              className={`text-sm font-bold ${scoreColor(headerScore)}`}
            >
              {headerScore}
            </span>
          </div>
        )}
      </div>

      {/* Film cards list */}
      <div className="space-y-4">
        {films.map((film, i) => (
          <FilmCard key={film.title || i} film={film} index={i} />
        ))}
      </div>

      {/* Market gap */}
      <MarketGapSection gap={gap} />

      {/* Positioning summary */}
      {positioningSummary && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="rounded-lg border border-white/10 bg-white/[0.03] p-4"
        >
          <div className="text-[10px] text-white/40 uppercase tracking-wider mb-2">
            Positioning Summary
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            {positioningSummary}
          </p>
        </motion.div>
      )}
    </section>
  );
}
